import { Build, Component, Element, Host, Prop, State, h } from '@stencil/core';

const COMPONENT_NAME = 'sn-navbar-type-1';

/**
 * Navbar component for SignNow brand navigation
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @contentConfig Navbar component for site navigation with dropdown menus
 * @slot area-logo - {required}, <me-logo src="..." href="/" alt="..."> - Logo area
 * @slot area-navbar - {required}, {multiple}, <me-button tag="a" variant="ghost" href="..."> - Desktop navigation items (also supports me-dropdown)
 * @slot area-actions - {required}, {multiple}, <me-button variant="primary" href="..."> - Desktop action buttons
 * @slot area-navbar-mobile - {required}, {multiple}, <me-button tag="a" variant="ghost" href="..."> - Mobile navigation items
 * @slot area-actions-mobile - {required}, {multiple}, <me-button variant="secondary" href="..."> - Mobile action buttons
 * @slot area-search - Optional search component override. Uses sn-navbar-search-type-1 by default.
 * @example
 *   <sn-navbar-type-1>
 *     <me-logo slot="area-logo" href="/" src="https://marketing-static.signnow.com/310/images/global/svg/signNow-logo-Colored.svg" alt="airSlate SignNow"></me-logo>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="/features">Features</me-button>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="/solutions">Solutions</me-button>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="/integrations">Integrations</me-button>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="https://snseats.signnow.com/pricing">Pricing</me-button>
 *     <me-button slot="area-actions" variant="secondary" href="/contact">Contact sales</me-button>
 *     <me-button slot="area-actions" variant="primary" href="https://snseats.signnow.com/purchase/business_free_trial/loggedout">Free trial</me-button>
 *     <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href="/features">Features</me-button>
 *     <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href="/solutions">Solutions</me-button>
 *     <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href="https://support.signnow.com">Support</me-button>
 *     <me-button slot="area-actions-mobile" variant="secondary" href="/contact">Contact sales</me-button>
 *     <me-button slot="area-actions-mobile" variant="primary" href="https://snseats.signnow.com/purchase/business_free_trial/loggedout">Free trial</me-button>
 *   </sn-navbar-type-1>
 */
@Component({
  tag: 'sn-navbar-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNNavbarType1 {
  @Element() el: HTMLElement;

  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Tracks if component is ready on client
   */
  @State() isClientReady: boolean = false;

  componentDidLoad() {
    // SSR Overlay Transition Strategy:
    // 1. On SSR: render empty Host with --ssr-deferred class (hidden via CSS)
    // 2. componentDidLoad: set isClientReady → triggers re-render with full navbar + --loading class
    // 3. componentDidUpdate: start transition to visible and hide placeholder
    if (Build.isBrowser) {
      this.isClientReady = true;
    }
  }

  componentDidUpdate() {
    if (
      Build.isBrowser &&
      this.isClientReady &&
      this.el.classList.contains('sn-navbar-type-1--loading') &&
      !this.el.classList.contains('sn-navbar-type-1--ready') &&
      !this.el.classList.contains('sn-navbar-type-1--final')
    ) {
      this.transitionToReady();
    }
  }

  private transitionToReady() {
    const innerNavbar = this.el.querySelector('navbar-type-1');
    const placeholder = document.querySelector('sn-navbar-placeholder-type-1');

    const makeReady = () => {
      const dropdowns = this.el.querySelectorAll('me-dropdown');
      const allDropdownsHydrated = Array.from(dropdowns).every(d => d.classList.contains('hydrated'));

      if (!allDropdownsHydrated && dropdowns.length > 0) {
        requestAnimationFrame(() => makeReady());
        return;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            placeholder?.setAttribute('hidden', '');
            this.el.classList.remove('sn-navbar-type-1--loading');
            this.el.classList.add('sn-navbar-type-1--ready');
          }, 50);
        });
      });
    };

    if (innerNavbar?.classList.contains('hydrated')) {
      makeReady();
    } else if (innerNavbar) {
      const observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (innerNavbar.classList.contains('hydrated')) {
              observer.disconnect();
              makeReady();
              break;
            }
          }
        }
      });
      observer.observe(innerNavbar, { attributes: true, attributeFilter: ['class'] });

      setTimeout(() => {
        observer.disconnect();
        if (!this.el.classList.contains('sn-navbar-type-1--ready') && !this.el.classList.contains('sn-navbar-type-1--final')) {
          makeReady();
        }
      }, 2000);
    }
  }

  private setSearchOpened(opened: boolean) {
    this.el.classList.toggle('sn-navbar-type-1--search-opened', opened);
  }

  private handleSearchOpenChange = (event: CustomEvent<{ opened: boolean }>) => {
    this.setSearchOpened(event.detail.opened);
  };

  private handleNavbarClick = (event: MouseEvent) => {
    if (!Build.isBrowser || window.innerWidth <= 960) return;

    const target = event.target as HTMLElement;
    const searchButton = target.closest("me-button[icon='interface--search']");

    if (!searchButton || !this.el.contains(searchButton)) return;

    event.preventDefault();

    const search = this.el.querySelector('sn-navbar-search-type-1') as (HTMLElement & { opened?: boolean }) | null;
    const nextOpened = !search?.opened;

    if (search) {
      search.opened = nextOpened;
    }

    this.setSearchOpened(nextOpened);
  };

  private hasCustomSearchSlot() {
    return Array.from(this.el.children).some(child => child.getAttribute('slot') === 'area-search' && !child.hasAttribute('data-default-navbar-search'));
  }

  render() {
    // SSR: render empty Host — placeholder handles SSR structure
    if (!this.isClientReady) {
      return <Host class="sn-navbar-type-1--ssr-deferred" data-color-mode={this.colorMode}></Host>;
    }

    return (
      <Host class={`${COMPONENT_NAME} ${COMPONENT_NAME}--loading`} data-color-mode={this.colorMode} onSnSearchOpenChange={this.handleSearchOpenChange}>
        <navbar-type-1 class={`${COMPONENT_NAME}--nav`} layout="logo-nav-first" sticky="always" mobileWidth="full" mobileBreakpoint={960} border={false} onClick={this.handleNavbarClick}>
          <slot name="area-logo"></slot>
          <slot name="area-navbar"></slot>
          <slot name="area-actions"></slot>
          <slot name="area-navbar-mobile"></slot>
          <slot name="area-actions-mobile"></slot>
          <slot name="area-search"></slot>
          {!this.hasCustomSearchSlot() && (
            <sn-navbar-search-type-1
              slot="area-search"
              data-default-navbar-search
              colorMode={this.colorMode}
              placeholder="Find anything (ie. integration, solutions, etc.)"
              searchLabel="Search"
              startSearchingLabel="to start searching"
              searchErrorLabel="This field may not be empty."
              suggestions={['Edit PDFs online', 'Send documents for signing', 'Create reusable templates', 'Send documents in bulk', 'All features']}
            ></sn-navbar-search-type-1>
          )}
        </navbar-type-1>
      </Host>
    );
  }
}
